import React from "react";
import { StyleSheet, Text, View, ViewProps } from "react-native";

import { CartContext } from "../../contexts/cart/Store";
import { AuthContext } from "../../contexts/auth/Store";
import { AnalyticsService } from "../../constants/Analytics";

import { OrderContext } from "../../contexts/order/Store";
import StyledButton from "../StyledButton";
import useOrderMeta from "../../hooks/useOrderMeta";
import { SafeJSONObject } from "../../constants/Utils";
import http from "../../utils/http";
import useMounted from "../../hooks/useMounted";
import AsyncStorage from "@react-native-async-storage/async-storage";
import GenericModal from "../modals/GenericModal";
import { useNavigation } from "@react-navigation/native";
import { formatCurrency } from "../../utils/money";
import FontStyles from "../../constants/FontStyle";
import OrderConfirmModal from "../modals/OrderConfirmModal";
import { orderGroupService } from "../../services/OrderGroupService";
import { IBuyer } from "../../interfaces/buyer";

const analytics = new AnalyticsService();

interface IProps extends ViewProps {
  callback?: VoidFunction;
}

const CompleteOrderButton: React.FC<IProps> = ({ callback, ...props }) => {
  const navigation = useNavigation();
  const auth = React.useContext(AuthContext);
  const token = auth.getUserData.userToken;
  const buyer: IBuyer = auth.getUserData.buyerData;
  const [cart_state, cart_dispatch, cart_fetch] = React.useContext(CartContext);
  const { action: order_fetch } = React.useContext(OrderContext);
  const isMounted = useMounted();

  const order_meta = useOrderMeta();
  const merged_order_meta = JSON.stringify(
    Object.assign(
      {},
      SafeJSONObject(cart_state.order_group?.meta),
      SafeJSONObject(order_meta)
    )
  );

  const [isSubmitting, setSubmitting] = React.useState(false);
  const [mov, setMov] = React.useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const hideModal = () => {
    isMounted.current && setOpenModal(!openModal);
  };
  const [confirmationMessage, setConfirmationMessage] =
    React.useState<string>();
  const [openConfirmationModal, setConfirmationModal] = React.useState(true);
  const toggleConfirmationModal = () => {
    isMounted.current && setConfirmationModal(!openConfirmationModal);
  };

  const processOrder = () => {
    setSubmitting(true);
    let group = cart_state.order_group;
    let form = new FormData();
    form.append("_id", group._id);
    form.append("meta", merged_order_meta);
    form.append("old_tag", "draft");
    form.append("new_tag", "pending");
    http
      .post("order_group/update_tag", form, token)
      .then((res) => {
        if (res.code === 200) {
          // show modal
          // set submitting false
          // clear
          try {
            const analyticsData = Object.assign(
              {},
              {
                ref: group.ref,
                amount: group.amount,
              }
            );
            analytics.logEvent("Placed Order", buyer, analyticsData);
            orderGroupService
              .fetchDeliveryEta(
                `${buyer?.location_id}`,
                Math.ceil(new Date().getTime() / 1000)
              )
              .then((data) => {
                isMounted.current && setConfirmationMessage(data);
              })
              .catch((error) => {
                isMounted.current &&
                  setConfirmationMessage(
                    `Thank you for your order on Suplias.\n\nYour Order should be delivered within 24hrs.`
                  );
              })
              .finally(() => {
                isMounted.current && setSubmitting(false);
                isMounted.current && setConfirmationModal(true);
                if (callback && typeof callback === "function") {
                  callback();
                }
                cart_dispatch({ type: "CLEAR_ALL" });
                cart_fetch()
                  .then(() => null)
                  .catch(() => null);
                order_fetch()
                  .then(() => null)
                  .catch(() => null);
              });
          } catch (err) {
            isMounted.current && setSubmitting(false);
            // ToastAndroid.show(err.message, ToastAndroid.SHORT, ToastAndroid.BOTTOM)
          }
        } else {
          cart_fetch()
            .then(() => null)
            .catch(() => null);
          order_fetch()
            .then(() => null)
            .catch(() => null);
          // ToastAndroid.show(res.message, ToastAndroid.SHORT, ToastAndroid.BOTTOM)
          isMounted.current && setSubmitting(false);
        }
      })
      .catch((err) => {
        isMounted.current && setSubmitting(false);
        // ToastAndroid.show(err.message, ToastAndroid.SHORT, ToastAndroid.BOTTOM)
      });
  };

  const verifyOrderPlacement = () => {
    if (mov > 0 && mov > cart_state.order_group.amount) {
      isMounted.current && setOpenModal(true);
    } else {
      processOrder();
    }
  };

  React.useEffect(() => {
    (async () => {
      const _mov = await AsyncStorage.getItem("MOV");
      if (_mov) {
        isMounted.current && setMov(parseFloat(_mov));
      }
    })();
    return () => {};
  }, []);

  return (
    <>
      {confirmationMessage && (
        <OrderConfirmModal
          toggleModal={toggleConfirmationModal}
          isOpen={openConfirmationModal}
          message={confirmationMessage}
        />
      )}
      <GenericModal
        content={
          <Text style={styles.contentText}>
            Minimum order amount is {formatCurrency(mov)}. Please add more
            items.
          </Text>
        }
        modalState={openModal}
        hideModal={hideModal}
        buttons={[
          {
            type: "default",
            label: "Add Products",
            fluid: false,
            action: () => {
              hideModal();
              navigation.navigate("Home");
            },
          },
        ]}
      />
      {cart_state &&
      cart_state.order_group_items &&
      cart_state.order_group_items.length ? (
        <View {...props}>
          <StyledButton
            label={`Complete Order`}
            color="default"
            style={{
              marginTop: 12,
              height: 36,
            }}
            submitting={isSubmitting}
            disabled={isSubmitting}
            action={verifyOrderPlacement}
          />
        </View>
      ) : null}
    </>
  );
};

export default CompleteOrderButton;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentText: {
    marginTop: 40,
    marginBottom: 20,
    ...FontStyles.size14,
    ...FontStyles.normal,
    ...FontStyles.lh18,
    ...FontStyles.dark,
    textAlign: "center",
  },
});
